import { ADDRESS_ZERO } from '@uniswap/router-sdk'
import { Currency, CurrencyAmount, Token } from '@uniswap/sdk-core'
import {
  createLpPosition,
  fetchSwap,
  increaseLpPosition,
  migrateLpPosition,
} from 'uniswap/src/data/apiClients/tradingApi/TradingApiClient'
import {
  CreateLPPositionRequest,
  CreateSwapRequest,
  DutchQuoteV2,
  IncreaseLPPositionRequest,
  MigrateLPPositionRequest,
  PriorityQuote,
} from 'uniswap/src/data/tradingApi/__generated__'
import {
  LiquidityTransactionType,
  LiquidityTxAndGasInfo,
  isValidLiquidityTxContext,
} from 'uniswap/src/features/transactions/liquidity/types'
import {
  ClassicSwapFlow,
  ClassicSwapSteps,
  DecreasePositionFlow,
  DecreasePositionSteps,
  DecreasePositionTransactionStep,
  IncreasePositionFlow,
  IncreasePositionSteps,
  IncreasePositionTransactionStep,
  IncreasePositionTransactionStepAsync,
  MigratePositionFlow,
  MigratePositionSteps,
  MigratePositionTransactionStep,
  MigratePositionTransactionStepAsync,
  Permit2SignatureStep,
  SwapTransactionStep,
  SwapTransactionStepAsync,
  TokenApprovalTransactionStep,
  TokenRevocationTransactionStep,
  TransactionStep,
  TransactionStepType,
  UniswapXSignatureStep,
  UniswapXSwapFlow,
  UniswapXSwapSteps,
  WrapTransactionStep,
} from 'uniswap/src/features/transactions/swap/types/steps'
import { SwapTxAndGasInfo, isValidSwapTxContext } from 'uniswap/src/features/transactions/swap/types/swapTxAndGasInfo'
import { isBridge, isClassic, isUniswapX } from 'uniswap/src/features/transactions/swap/utils/routing'
import {
  ValidatedPermit,
  ValidatedTransactionRequest,
  validateTransactionRequest,
} from 'uniswap/src/features/transactions/swap/utils/trade'
import { parseERC20ApproveCalldata } from 'uniswap/src/utils/approvals'

function orderSwapSteps(flow: ClassicSwapFlow): ClassicSwapSteps[] {
  const steps: ClassicSwapSteps[] = []

  if (flow.revocation) {
    steps.push(flow.revocation)
  }

  if (flow.approval) {
    steps.push(flow.approval)
  }

  if (flow.permit) {
    steps.push(flow.permit)
  }

  steps.push(flow.swap)

  return steps
}

function orderIncreaseLiquiditySteps(flow: IncreasePositionFlow): IncreasePositionSteps[] {
  const steps: IncreasePositionSteps[] = []

  if (flow.approvalToken0) {
    steps.push(flow.approvalToken0)
  }

  if (flow.approvalToken1) {
    steps.push(flow.approvalToken1)
  }

  if (flow.approvalPositionToken) {
    steps.push(flow.approvalPositionToken)
  }

  if (flow.permit) {
    steps.push(flow.permit)
  }

  steps.push(flow.increasePosition)

  return steps
}

function orderMigrateLiquiditySteps(flow: MigratePositionFlow): MigratePositionSteps[] {
  const steps: MigratePositionSteps[] = []

  if (flow.permit) {
    steps.push(flow.permit)
  }

  steps.push(flow.migrate)

  return steps
}

function orderDecreaseLiquiditySteps(flow: DecreasePositionFlow): DecreasePositionSteps[] {
  const steps: DecreasePositionSteps[] = []

  if (flow.approvalPositionToken) {
    steps.push(flow.approvalPositionToken)
  }

  steps.push(flow.decreasePosition)

  return steps
}

function orderUniswapXSteps(flow: UniswapXSwapFlow): UniswapXSwapSteps[] {
  const steps: UniswapXSwapSteps[] = []

  if (flow.wrap) {
    steps.push(flow.wrap)
  }

  if (flow.revocation) {
    steps.push(flow.revocation)
  }

  if (flow.approval) {
    steps.push(flow.approval)
  }

  steps.push(flow.signOrder)

  return steps
}

function createWrapTransactionStep(
  txRequest: ValidatedTransactionRequest | undefined,
  inputAmount: CurrencyAmount<Currency> | undefined,
): WrapTransactionStep | undefined {
  return txRequest && inputAmount
    ? { txRequest, type: TransactionStepType.WrapTransaction, amount: inputAmount }
    : undefined
}

function createApprovalTransactionStep(
  txRequest: ValidatedTransactionRequest | undefined,
  amountIn?: CurrencyAmount<Currency>,
  pair?: [Currency, Currency],
): TokenApprovalTransactionStep | undefined {
  if (!txRequest?.data || !amountIn) {
    return undefined
  }

  // 🔧 修复：跳过目标地址为零地址的 approval 请求
  if (!txRequest.to || txRequest.to === '0x0000000000000000000000000000000000000000') {
    return undefined
  }

  const type = TransactionStepType.TokenApprovalTransaction
  const token = amountIn.currency.wrapped
  const { spender } = parseERC20ApproveCalldata(txRequest.data.toString())
  const amount = amountIn.quotient.toString()

  return { type, txRequest, token, spender, amount, pair }
}

function createRevocationTransactionStep(
  txRequest: ValidatedTransactionRequest | undefined,
  token: Token,
): TokenRevocationTransactionStep | undefined {
  if (!txRequest?.data) {
    return undefined
  }

  // 🔧 修复：跳过目标地址为零地址的 revocation 请求
  if (!txRequest.to || txRequest.to === '0x0000000000000000000000000000000000000000') {
    return undefined
  }

  const type = TransactionStepType.TokenRevocationTransaction
  const { spender, amount } = parseERC20ApproveCalldata(txRequest.data.toString())

  if (amount !== BigInt(0)) {
    return undefined
  }

  return { type, txRequest, token, spender, amount: '0' }
}

function createSignOrderUniswapXStep(
  permitData: ValidatedPermit,
  quote: DutchQuoteV2 | PriorityQuote,
): UniswapXSignatureStep {
  return { type: TransactionStepType.UniswapXSignature, deadline: quote.orderInfo.deadline, quote, ...permitData }
}

function createPermit2SignatureStep(permitData: ValidatedPermit, token: Currency): Permit2SignatureStep {
  return { type: TransactionStepType.Permit2Signature, token, ...permitData }
}

function createSwapTransactionStep(txRequest: ValidatedTransactionRequest): SwapTransactionStep {
  // 🔧 修复：验证 swap 交易目标地址
  if (!txRequest.to || txRequest.to === '0x0000000000000000000000000000000000000000') {
    throw new Error('Invalid swap transaction: target address is zero address')
  }
  return { type: TransactionStepType.SwapTransaction, txRequest }
}

function createSwapTransactionAsyncStep(
  swapRequestArgs: CreateSwapRequest | undefined,
  v4Enabled: boolean,
): SwapTransactionStepAsync {
  // 🔧 防重复调用保护
  let isExecuting = false
  const requestCache = new Map<string, ValidatedTransactionRequest>()
  
  return {
    type: TransactionStepType.SwapTransactionAsync,
    getTxRequest: async (signature: string): Promise<ValidatedTransactionRequest | undefined> => {
      if (!swapRequestArgs) {
        return undefined
      }

      if (!signature) {
        return undefined
      }

      // 🔧 生成缓存键
      const requestId = (swapRequestArgs.quote as any)?.requestId || 'no-id'
      const cacheKey = `${signature}_${requestId}`
      
      // 🔧 检查缓存
      if (requestCache.has(cacheKey)) {
        return requestCache.get(cacheKey)
      }

      // 🔧 防重复调用保护
      if (isExecuting) {
        throw new Error('fetchSwap already in progress')
      }

      isExecuting = true


      try {
        // 🔍 构建最终的 fetchSwap 参数
        const finalSwapParams = {
          ...swapRequestArgs,
          signature,
          /* simulating transaction provides a more accurate gas limit, and the simulation will succeed because async swap step will only occur after approval has been confirmed. */
          simulateTransaction: true,
          v4Enabled,
        }


        const response = await fetchSwap(finalSwapParams)

        // 🚀 NEW: 支持新的 API 响应格式 { calls, chainId, from, gasFee, requestId }
        let swapTransaction
        if (response.calls && response.calls.length > 0) {
          // 新格式：从 calls[0] 提取交易数据
          const firstCall = response.calls[0]

          if (firstCall && firstCall.to) {
            // 🔧 安全地转换 chainId
            const chainId = typeof response.chainId === 'number' 
              ? response.chainId 
              : Number(response.chainId)

            
            if (isNaN(chainId) || chainId < 0) {
              throw new Error(`无效的 chainId: ${response.chainId}`)
            } else {
              swapTransaction = {
                to: firstCall.to,
                data: firstCall.data,
                value: firstCall.value || '0x00',
                chainId: chainId,
                from: response.from,
                // 🆕 优先使用 API 返回的 gasLimit
                ...(response.gasLimit && { gasLimit: response.gasLimit }),
                // 🚨 注意：gasFee 和 gasEstimates 不是标准的以太坊交易字段，不应包含在交易对象中
              }
            }
          } else {
            throw new Error('交易调用数据缺失目标地址')
          }
        } else {
          // 原始格式：直接使用 swap 字段 (向后兼容)
          swapTransaction = response.swap
        }


        // 🚨 CRITICAL FIX: 在验证前检查必要字段，防止 'Cannot read properties of undefined' 错误
        if (!swapTransaction) {
          throw new Error('交换交易数据为空，请重试')
        }

        if (!swapTransaction.to) {
          throw new Error('交换交易缺少目标地址')
        }

        const validatedRequest = validateTransactionRequest(swapTransaction)

        // 🔧 缓存结果
        if (validatedRequest) {
          requestCache.set(cacheKey, validatedRequest)
        } else {

        }

        return validatedRequest
      } catch (error: unknown) {
        throw error
      } finally {
        // 🔧 无论成功还是失败，都要重置执行状态
        isExecuting = false
      }
    },
  }
}

function createIncreasePositionStep(txRequest: ValidatedTransactionRequest): IncreasePositionTransactionStep {
  return {
    type: TransactionStepType.IncreasePositionTransaction,
    txRequest,
  }
}

function createCreatePositionAsyncStep(
  createPositionRequestArgs: CreateLPPositionRequest | undefined,
): IncreasePositionTransactionStepAsync {
  return {
    type: TransactionStepType.IncreasePositionTransactionAsync,

    getTxRequest: async (signature: string): Promise<ValidatedTransactionRequest | undefined> => {
      if (!createPositionRequestArgs) {
        return undefined
      }

      const { create } = await createLpPosition({
        ...createPositionRequestArgs,
        signature,
        simulateTransaction: true,
      })

      return validateTransactionRequest(create)
    },
  }
}

function createIncreasePositionAsyncStep(
  increasePositionRequestArgs: IncreaseLPPositionRequest | undefined,
): IncreasePositionTransactionStepAsync {
  return {
    type: TransactionStepType.IncreasePositionTransactionAsync,

    getTxRequest: async (signature: string): Promise<ValidatedTransactionRequest | undefined> => {
      if (!increasePositionRequestArgs) {
        return undefined
      }

      const { increase } = await increaseLpPosition({
        ...increasePositionRequestArgs,
        signature,
        simulateTransaction: true,
      })

      return validateTransactionRequest(increase)
    },
  }
}

function createDecreasePositionStep(txRequest: ValidatedTransactionRequest): DecreasePositionTransactionStep {
  return {
    type: TransactionStepType.DecreasePositionTransaction,
    txRequest,
  }
}

function createMigratePositionStep(txRequest: ValidatedTransactionRequest): MigratePositionTransactionStep {
  return {
    type: TransactionStepType.MigratePositionTransactionStep,
    txRequest,
  }
}

function createMigratePositionAsyncStep(
  migratePositionRequestArgs: MigrateLPPositionRequest | undefined,
  signatureDeadline: number | undefined,
): MigratePositionTransactionStepAsync {
  return {
    type: TransactionStepType.MigratePositionTransactionStepAsync,
    getTxRequest: async (signature: string): Promise<ValidatedTransactionRequest | undefined> => {
      if (!migratePositionRequestArgs || !signatureDeadline) {
        return undefined
      }

      const { migrate } = await migrateLpPosition({
        ...migratePositionRequestArgs,
        signature,
        signatureDeadline,
      })

      return validateTransactionRequest(migrate)
    },
  }
}

export function generateTransactionSteps(
  txContext: SwapTxAndGasInfo | LiquidityTxAndGasInfo,
  v4Enabled = false,
): TransactionStep[] {

  const isValidSwap = isValidSwapTxContext(txContext)
  const isValidLP = isValidLiquidityTxContext(txContext)
  


  if (isValidLP) {
    if (txContext.type === LiquidityTransactionType.Collect) {
      return [
        {
          type: TransactionStepType.CollectFeesTransactionStep,
          txRequest: txContext.txRequest,
        },
      ]
    }

    const { action, approveToken0Request, approveToken1Request, approvePositionTokenRequest } = txContext

    const approvalToken0 = createApprovalTransactionStep(approveToken0Request, action.currency0Amount)
    const approvalToken1 = createApprovalTransactionStep(approveToken1Request, action.currency1Amount)
    const approvalPositionToken = createApprovalTransactionStep(
      approvePositionTokenRequest,
      action.liquidityToken ? CurrencyAmount.fromRawAmount(action.liquidityToken, 1) : undefined,
      [action.currency0Amount.currency, action.currency1Amount.currency],
    )

    switch (txContext.type) {
      case 'decrease':
        return orderDecreaseLiquiditySteps({
          approvalPositionToken,
          decreasePosition: createDecreasePositionStep(txContext.txRequest),
        })
      case 'migrate':
        if (txContext.unsigned) {
          return orderMigrateLiquiditySteps({
            permit: createPermit2SignatureStep(
              txContext.permit,
              // V3->V4 migration requires a permit for the position token which is an NFT.
              // TODO (WEB-4920): Use the proper v3 Position NFT contract and chainId for given chain.
              // For now this placeholder is fine since it's not shown in the UI.
              new Token(1, ADDRESS_ZERO, 1, 'Uniswap V3 Positions NFT-V1', 'Uniswap V3 Positions NFT-V1'),
            ),
            migrate: createMigratePositionAsyncStep(
              txContext.migratePositionRequestArgs,
              txContext.permit.values.deadline as number,
            ),
          })
        } else {
          return orderMigrateLiquiditySteps({
            permit: undefined,
            migrate: createMigratePositionStep(txContext.txRequest),
          })
        }
      case 'create':
      case 'increase':
        if (txContext.unsigned) {
          return orderIncreaseLiquiditySteps({
            approvalToken0,
            approvalToken1,
            approvalPositionToken,
            permit: createPermit2SignatureStep(txContext.permit, action.currency0Amount.currency), // TODO: what about for multiple tokens
            increasePosition:
              txContext.type === 'increase'
                ? createIncreasePositionAsyncStep(txContext.increasePositionRequestArgs)
                : createCreatePositionAsyncStep(txContext.createPositionRequestArgs),
          })
        } else {
          return orderIncreaseLiquiditySteps({
            approvalToken0,
            approvalToken1,
            approvalPositionToken,
            permit: undefined,
            increasePosition: createIncreasePositionStep(txContext.txRequest),
          })
        }
    }
  } else if (isValidSwap) {
    const { trade, approveTxRequest, revocationTxRequest } = txContext


    const revocation = createRevocationTransactionStep(revocationTxRequest, trade.inputAmount.currency.wrapped)
    const approval = createApprovalTransactionStep(approveTxRequest, trade.inputAmount)

    if (isClassic(txContext)) {
      const { swapRequestArgs } = txContext


      if (txContext.unsigned) {
        return orderSwapSteps({
          revocation,
          approval,
          permit: createPermit2SignatureStep(txContext.permit, trade.inputAmount.currency),
          swap: createSwapTransactionAsyncStep(swapRequestArgs, v4Enabled),
        })
      }

      // 🔧 修复：如果 txRequest 不存在但需要它，返回空数组
      // 这会导致按钮显示 loading 状态而不是报错
      if (!txContext.txRequest) {
        return []
      }

      return orderSwapSteps({
        revocation,
        approval,
        permit: undefined,
        swap: createSwapTransactionStep(txContext.txRequest),
      })
    } else if (isUniswapX(txContext)) {
      return orderUniswapXSteps({
        revocation,
        approval,
        wrap: createWrapTransactionStep(txContext.wrapTxRequest, trade.inputAmount),
        signOrder: createSignOrderUniswapXStep(txContext.permit, txContext.trade.quote.quote),
      })
    } else if (isBridge(txContext)) {
      const { swapRequestArgs } = txContext

      if (txContext.unsigned) {
        return orderSwapSteps({
          revocation,
          approval,
          permit: createPermit2SignatureStep(txContext.permit, trade.inputAmount.currency),
          swap: createSwapTransactionAsyncStep(swapRequestArgs, v4Enabled),
        })
      }
      
      // 🔧 修复：如果 txRequest 不存在但需要它，返回空数组
      // 这会导致按钮显示 loading 状态而不是报错
      if (!txContext.txRequest) {
        return []
      }
      
      return orderSwapSteps({
        revocation,
        approval,
        permit: undefined,
        swap: createSwapTransactionStep(txContext.txRequest),
      })
    }
  }

  return []
}
